import useSWR from 'swr'
import { getStoryBySlug, useStoryblokState } from '@cinch-nx/data-storyblok'
import {
  NewCarsFinanceFigures,
  UsedCarsFinanceFigures,
} from '@cinch-nx/ui-finance'
import { CarDomain } from '../../utils'
import { useSearchStore } from '..'

export interface UsedCarsFinRepStoryContent {
  component: 'UsedCarsFinanceRepresentativeExample'
  cashPrice: string
  creditAmount: string
  customerDeposit: string
  representativeApr: string
  annualMileageLimit: string
  totalAmountPayable: string
  optionToPurchaseFee: string
  totalInterestAmount: string
  optionalFinalPayment: string
  monthlyPaymentsAmount: string
  fixedAnnualInterestRate: string
  numberOfMonthlyPayments: string
  excessMileageChargeInPence: string
}

// storyblok returns the fields as strings, but the fin rep components expect numbers.
// Despite this, they will work (because the strings get cast to numbers anyway),
// but the transform function keeps TS happy about it
export const transformUsedCarsFinRepStoryContentToProps = ({
  component,
  cashPrice,
  creditAmount,
  customerDeposit,
  representativeApr,
  annualMileageLimit,
  totalAmountPayable,
  optionToPurchaseFee,
  totalInterestAmount,
  optionalFinalPayment,
  monthlyPaymentsAmount,
  fixedAnnualInterestRate,
  numberOfMonthlyPayments,
  excessMileageChargeInPence,
}: UsedCarsFinRepStoryContent): UsedCarsFinanceFigures & {
  component: 'UsedCarsFinanceRepresentativeExample'
} => ({
  component,
  cashPrice: Number(cashPrice),
  creditAmount: Number(creditAmount),
  customerDeposit: Number(customerDeposit),
  representativeApr: Number(representativeApr),
  annualMileageLimit: Number(annualMileageLimit),
  totalAmountPayable: Number(totalAmountPayable),
  optionToPurchaseFee: Number(optionToPurchaseFee),
  totalInterestAmount: Number(totalInterestAmount),
  optionalFinalPayment: Number(optionalFinalPayment),
  monthlyPaymentsAmount: Number(monthlyPaymentsAmount),
  fixedAnnualInterestRate: Number(fixedAnnualInterestRate),
  numberOfMonthlyPayments: Number(numberOfMonthlyPayments),
  excessMileageChargeInPence: Number(excessMileageChargeInPence),
})

export interface NewCarsFinRepStoryContent {
  component: 'FinanceRepresentativeExample'
  annualMileageLimit: string
  creditAmount: string
  customerDeposit?: string
  excessMileageChargeInPence: string
  financeDepositContribution?: string
  fixedAnnualInterestRate: string
  monthlyPaymentsAmount: string
  numberOfMonthlyPayments: string
  onTheRoadPrice: string
  optionalFinalPayment: string
  optionToPurchaseFee?: string
  representativeApr: string
  retailerDiscount?: string
  testDriveVoucher?: string
  totalAmountPayable: string
  totalInterestAmount: string
}

// storyblok returns the fields as strings, but the fin rep components expect numbers.
// Despite this, they will work (because the strings get cast to numbers anyway),
// but the transform function keeps TS happy about it
export const transformNewCarsFinRepStoryContentToProps = ({
  component,
  annualMileageLimit,
  creditAmount,
  customerDeposit,
  excessMileageChargeInPence,
  financeDepositContribution,
  fixedAnnualInterestRate,
  monthlyPaymentsAmount,
  numberOfMonthlyPayments,
  onTheRoadPrice,
  optionalFinalPayment,
  optionToPurchaseFee,
  representativeApr,
  retailerDiscount,
  testDriveVoucher,
  totalAmountPayable,
  totalInterestAmount,
}: NewCarsFinRepStoryContent): NewCarsFinanceFigures & {
  component: 'FinanceRepresentativeExample'
} => ({
  component,
  annualMileageLimit: Number(annualMileageLimit),
  creditAmount: Number(creditAmount),
  customerDeposit: customerDeposit ? Number(customerDeposit) : undefined,
  excessMileageChargeInPence: Number(excessMileageChargeInPence),
  financeDepositContribution: financeDepositContribution
    ? Number(financeDepositContribution)
    : undefined,
  fixedAnnualInterestRate: Number(fixedAnnualInterestRate),
  monthlyPaymentsAmount: Number(monthlyPaymentsAmount),
  numberOfMonthlyPayments: Number(numberOfMonthlyPayments),
  onTheRoadPrice: Number(onTheRoadPrice),
  optionalFinalPayment: Number(optionalFinalPayment),
  optionToPurchaseFee: optionToPurchaseFee
    ? Number(optionToPurchaseFee)
    : undefined,
  representativeApr: Number(representativeApr),
  retailerDiscount: retailerDiscount ? Number(retailerDiscount) : undefined,
  testDriveVoucher: testDriveVoucher ? Number(testDriveVoucher) : undefined,
  totalAmountPayable: Number(totalAmountPayable),
  totalInterestAmount: Number(totalInterestAmount),
})

const fetcher = async ({
  slug,
  isInsideStoryblok,
}: {
  slug: string
  isInsideStoryblok: boolean
}) => {
  const args: Parameters<typeof getStoryBySlug>[1] = {
    version: isInsideStoryblok ? 'draft' : 'published',
    ...(isInsideStoryblok && { cv: Date.now() }),
  }

  return await getStoryBySlug(slug, args, { strict: false })
}

export function useFinRepStoryblokData(page: CarDomain) {
  const isInsideStoryblok = useSearchStore<boolean>((s) => s.isInsideStoryblok)

  const slug = `finance-representative-examples/master-${page}-finance-representative-example`

  // See: https://swr.vercel.app/docs/conditional-fetching#conditional
  // and: https://swr.vercel.app/docs/arguments#multiple-arguments
  // For information on the advanced usage used here
  const { data, error, isLoading, isValidating } = useSWR(
    [slug, isInsideStoryblok],
    ([slug, isInsideStoryblok]) => fetcher({ slug, isInsideStoryblok }),
  )

  const story = useStoryblokState({
    story: data || undefined,
    enableBridge: isInsideStoryblok,
  })

  const content = story?.content as unknown as
    | UsedCarsFinRepStoryContent
    | NewCarsFinRepStoryContent
  return {
    finRep: content,
    isLoading,
    isValidating,
    error,
  }
}
